import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { LoaderCircle, TriangleAlert, X } from 'lucide-react';
import { useT } from '@/i18n';
import { ApiError } from '@/lib/api/client';
import { CHANNEL_META } from '@/lib/constants';
import { avatarColour, initials, textOn } from '@/lib/color';
import type { Channel } from '@/lib/api/types';
import { isTenantBrand, safeAssetUrl, useBranding } from '@/lib/branding';
import { decisionT } from '@castfeeds/i18n/decisions';

/** Left-to-right island for timecodes, emails, URLs, file names and numbers inside RTL text. */
export function Ltr({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span dir="ltr" className={className} style={{ unicodeBidi: 'isolate' }}>
      {children}
    </span>
  );
}

export function Spinner({ size = 16, label }: { size?: number; label?: string }) {
  return (
    <span role="status" className="row" style={{ gap: 6 }}>
      <LoaderCircle size={size} className="spin" aria-hidden />
      {label ? <span>{label}</span> : <span className="sr-only">…</span>}
    </span>
  );
}

/** Castfeeds mark + wordmark (logo route A from design/CastfeedsLogo.dc.html). The logo never mirrors. */
/**
 * The product's mark: the Castfeeds wordmark, or on a tenant's own domain (white-label) the tenant's logo
 * (a dark-theme variant when it has one) or its product name.
 */
export function Wordmark({ size = 16 }: { size?: number }) {
  const branding = useBranding((s) => s.branding);
  if (isTenantBrand(branding)) {
    const light = safeAssetUrl(branding.logo_url);
    const dark = safeAssetUrl(branding.logo_dark_url);
    const height = Math.round(size * 1.6);
    if (light || dark) {
      return (
        <span className="row" style={{ lineHeight: 1, flex: 'none' }} aria-label={branding.product_name} role="img">
          {light ? <img className={`brand-logo-light${dark ? ' has-dark' : ''}`} src={light} alt="" style={{ height, maxWidth: size * 10, objectFit: 'contain' }} /> : null}
          {dark ? <img className={`brand-logo-dark${light ? '' : ' only'}`} src={dark} alt="" style={{ height, maxWidth: size * 10, objectFit: 'contain' }} /> : null}
        </span>
      );
    }
    return (
      <span className="display" style={{ fontWeight: 700, fontSize: size * 1.05, letterSpacing: '-0.01em', color: 'var(--text)', flex: 'none' }}>
        {branding.product_name}
      </span>
    );
  }
  return <CastfeedsWordmark size={size} />;
}

/** "Powered by Castfeeds" on a tenant's domain, unless the tenant has the add-on that removes it. */
export function PoweredBy({ className = 'muted small' }: { className?: string }) {
  const { t } = useT();
  const branding = useBranding((s) => s.branding);
  if (!isTenantBrand(branding) || !branding.powered_by) return null;
  return <span className={className}>{t('wlPoweredBy')}</span>;
}

export function CastfeedsWordmark({ size = 16 }: { size?: number }) {
  const scale = size / 19.2;
  return (
    <span dir="ltr" className="row" style={{ gap: 6 * scale, lineHeight: 1, flex: 'none' }} aria-label="Castfeeds">
      <svg width={48.4 * scale} height={24 * scale} viewBox="0 0 48.4 24" aria-hidden style={{ display: 'block' }}>
        <rect x="12" y="0" width="13.5" height="24" rx="3" fill="var(--text)" />
        <rect x="27.5" y="2.4" width="10.8" height="19.2" rx="3" fill="var(--text)" />
        <rect x="40.3" y="4.8" width="8.1" height="14.4" rx="3" fill="var(--text)" />
        <circle cx="5" cy="12" r="4.2" fill="#FF4A2E" />
      </svg>
      <span className="display" style={{ fontWeight: 700, fontSize: size * 1.05, letterSpacing: '-0.02em', color: 'var(--text)' }}>
        castfeeds
      </span>
    </span>
  );
}

export function Avatar({ name, colour, size = 30, title }: { name: string; colour?: string; size?: number; title?: string }) {
  return (
    <span className="avatar" title={title ?? name} style={{ width: size, height: size, background: colour ?? avatarColour(name), fontSize: Math.max(9, size * 0.36) }}>
      {initials(name)}
    </span>
  );
}

/** Coloured square with initials, used for customers and projects (brand primary as background). */
export function Tile({ name, colour, size = 36 }: { name: string; colour: string; size?: number }) {
  return (
    <span className="tile" aria-hidden style={{ width: size, height: size, background: colour, color: textOn(colour), fontSize: Math.round(size * 0.36), borderRadius: size > 44 ? 10 : 6 }}>
      {initials(name)}
    </span>
  );
}

export function ChannelChip({ channel }: { channel: Channel }) {
  const meta = CHANNEL_META[channel];
  if (!meta) return <span className="tag">{channel}</span>;
  return (
    <span dir="ltr" title={meta[0]} className="mono" style={{ fontSize: 10, fontWeight: 500, padding: '2px 6px', borderRadius: 4, background: meta[2], color: '#121318' }}>
      {meta[1]}
    </span>
  );
}

type PillTone = 'amber' | 'blue' | 'red' | 'green' | 'neutral' | 'muted';
const TONES: Record<PillTone, [string, string]> = {
  amber: ['var(--amber-soft)', 'var(--amber)'],
  blue: ['var(--blue-soft)', 'var(--blue-text)'],
  red: ['var(--red-soft)', 'var(--accent-text)'],
  green: ['var(--green-soft)', 'var(--green)'],
  neutral: ['var(--raise)', 'var(--text)'],
  muted: ['var(--raise)', 'var(--muted)'],
};
const STATUS_TONE: Record<string, PillTone> = {
  review: 'amber', client: 'blue', changes: 'red', approved: 'green', published: 'green', scheduled: 'blue', processing: 'neutral', draft: 'muted',
  proposed: 'blue', rejected: 'muted', rendering: 'neutral', failed: 'red', publishing: 'blue',
};

/** Status pill with the prototype's colours (ST map). */
export function StatusPill({ status, label, title }: { status: string; label?: string; title?: string }) {
  const { td } = useT();
  const [bg, fg] = TONES[STATUS_TONE[status] ?? 'neutral'];
  return (
    <span className="pill" style={{ background: bg, color: fg }} title={title}>
      {label ?? td(`st_${status}`, status)}
    </span>
  );
}

export function Toggle({ checked, onChange, label, sub, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; sub?: ReactNode; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="toggle" disabled={disabled} onClick={() => onChange(!checked)}>
      <span className="toggle-track" aria-hidden />
      <span className="stack" style={{ gap: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{label}</span>
        {sub ? <span className="muted small" style={{ lineHeight: '17px' }}>{sub}</span> : null}
      </span>
    </button>
  );
}

/** Single-choice button group (radio semantics). */
export function Segmented<T extends string | number>({ value, options, onChange, label, size, dirLtr }: { value: T; options: { value: T; label: ReactNode; title?: string }[]; onChange: (v: T) => void; label: string; size?: 'sm'; dirLtr?: boolean }) {
  return (
    <div role="radiogroup" aria-label={label} className={`segmented${size === 'sm' ? ' segmented-sm' : ''}`} dir={dirLtr ? 'ltr' : undefined}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" role="radio" aria-checked={o.value === value} title={o.title} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Pill-shaped single-choice chips (hook style, music mood, pace, customer filter). */
export function ChipGroup<T extends string>({ value, options, onChange, label, size }: { value: T; options: { value: T; label: ReactNode; swatch?: string }[]; onChange: (v: T) => void; label: string; size?: 'sm' }) {
  return (
    <div role="radiogroup" aria-label={label} className="row wrap" style={{ gap: 6 }}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} className={`chip${size === 'sm' ? ' chip-sm' : ''}`} onClick={() => onChange(o.value)}>
          {o.swatch ? <span className="swatch" style={{ width: 7, height: 7, background: o.swatch }} /> : null}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, hint, error, children }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode }) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id} style={{ fontWeight: 500 }}>{label}</label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': hint || error ? hintId : undefined })}
      {error ? (
        <span id={hintId} className="field-error">{error}</span>
      ) : hint ? (
        <span id={hintId} className="field-hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Modal({ title, onClose, children, footer, wide }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const { t } = useT();
  // Keep the latest onClose in a ref so the effect below runs once per opening: callers pass inline closures, and
  // re-running the effect on every parent render moved focus back to the first field on each keystroke.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key === 'Tab' && el) {
        const nodes = [...el.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input, select, textarea, [tabindex]:not([tabindex="-1"])')];
        if (nodes.length === 0) return;
        const [a, z] = [nodes[0], nodes[nodes.length - 1]];
        if (e.shiftKey && document.activeElement === a) {
          e.preventDefault();
          z.focus();
        } else if (!e.shiftKey && document.activeElement === z) {
          e.preventDefault();
          a.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={wide ? { width: 'min(860px, 100%)' } : undefined}>
        <div className="card-head">
          <h2 id={titleId} className="h2" style={{ fontSize: 16 }}>{title}</h2>
          <button type="button" data-close className="btn btn-ghost btn-icon push" aria-label={t('close')} onClick={onClose}>
            <X size={16} aria-hidden />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** Human message for an API error, in the UI language when we know the case. */
export function useErrorText() {
  const { t, locale } = useT();
  return (e: unknown): string => {
    if (e instanceof ApiError) {
      if (e.isNetwork) return t('errNetwork');
      // Amendment 24 codes, in the user's language
      const code = (e.body as { code?: string } | null)?.code;
      if (code === 'multitrack_undecided') return t('mtPending');
      if (code === 'processing_started') return t('mtProcessingStarted');
      // Approval decisions are a compare-and-set (WS Z): the video changed, or someone else decided first.
      if (code === 'version_conflict' && e.status === 409) return decisionT(locale)('decVersionConflict', { version: (e.body as { current_version?: number }).current_version ?? '?' });
      if (code === 'stage_conflict' && e.status === 409) return decisionT(locale)('decStageConflict');
      if (e.status === 403) return e.message || t('errForbidden');
      return e.message || t('errGeneric');
    }
    return t('errGeneric');
  };
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useT();
  const text = useErrorText()(error);
  return (
    <div role="alert" className="alert alert-error row" style={{ alignItems: 'flex-start' }}>
      <TriangleAlert size={16} aria-hidden style={{ flex: 'none', marginTop: 2 }} />
      <span style={{ flex: 1 }}>{text}</span>
      {onRetry ? (
        <button type="button" className="btn btn-sm" onClick={onRetry}>
          {t('retry')}
        </button>
      ) : null}
    </div>
  );
}

/** Renders loading / error states for a TanStack query and the content when data is ready. */
export function QueryView<T>({ query, children, loading }: { query: { data: T | undefined; isLoading: boolean; error: unknown; refetch: () => unknown }; children: (data: T) => ReactNode; loading?: ReactNode }) {
  const { t } = useT();
  if (query.isLoading) return <div style={{ padding: 18 }}>{loading ?? <Spinner label={t('loading')} />}</div>;
  if (query.error) return <div style={{ padding: 18 }}><ErrorBox error={query.error} onRetry={() => query.refetch()} /></div>;
  if (query.data === undefined) return null;
  return <>{children(query.data)}</>;
}

export function CopyButton({ text, label, className = 'btn btn-inv' }: { text: string; label: string; className?: string }) {
  const { t } = useT();
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch (e) {
      console.error('[web:app] clipboard write failed', e);
      window.prompt(label, text);
    }
  };
  return (
    <button type="button" className={className} onClick={copy} aria-live="polite">
      {done ? `${t('copied')} ✓` : label}
    </button>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}
